import React from 'react';
import {
  Badge,
  HoverCardContent,
  HoverCardRoot,
  HoverCardTrigger,
  Text,
} from '@/components/ui';
import { IconAlertTriangle, IconCheck, IconX } from '@tabler/icons-react';
import { FileMappingStatus } from '../utils/mappingStatus';

interface StatusBadgeProps {
  index: string;
  fileMappingStatus: FileMappingStatus;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Legacy component; selectedForm shape is not important for rendering.
  selectedForm: any; // StudyForm | null
}

export const MappingStatusBadge: React.FC<StatusBadgeProps> = ({
  index,
  fileMappingStatus,
  selectedForm,
}) => {
  return (
    <HoverCardRoot>
      <HoverCardTrigger>
        <Badge
          style={{ position: 'sticky', top: '0px', zIndex: '2' }}
          size="small"
          className={`file-mapping-status--${fileMappingStatus.status}`}
          variant={
            fileMappingStatus.status === 'excellent'
              ? 'surface'
              : fileMappingStatus.status === 'unmapped'
                ? 'solid'
                : 'surface'
            // fileMappingStatus.status === 'excellent' ? 'surface' : 'surface'
          }
          color={
            fileMappingStatus.status === 'excellent'
              ? 'success'
              : fileMappingStatus.status === 'unmapped'
                ? 'error'
                : 'warning'
          }
          endIcon={
            fileMappingStatus.status === 'excellent' ? (
              <IconCheck />
            ) : fileMappingStatus.status === 'unmapped' ? (
              <IconX />
            ) : (
              <IconAlertTriangle />
            )
          }
        >
          {index}
        </Badge>
      </HoverCardTrigger>
      <HoverCardContent>
        {/* File Status Badge */}
        {selectedForm && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'start',
              gap: '8px',
            }}
          >
            <Badge
              startIcon={
                (fileMappingStatus.status === 'excellent' && <IconCheck />) ||
                ((fileMappingStatus.status === 'missing-required' ||
                  fileMappingStatus.status === 'missing-required-metadata') && (
                  <IconX />
                ))
              }
              color={
                fileMappingStatus.status === 'excellent'
                  ? 'success'
                  : fileMappingStatus.status === 'good'
                    ? 'info'
                    : fileMappingStatus.status === 'missing-required' ||
                        fileMappingStatus.status === 'missing-required-metadata'
                      ? 'error'
                      : fileMappingStatus.status === 'partial'
                        ? 'warning'
                        : 'error'
              }
              variant={
                fileMappingStatus.status === 'excellent' ? 'soft' : 'outlined'
              }
            >
              {fileMappingStatus.status === 'excellent' && 'Ready to Import!'}
              {fileMappingStatus.status === 'good' &&
                `${Math.round(fileMappingStatus.score * 100)}% - Good Match`}
              {fileMappingStatus.status === 'missing-required' &&
                'Missing Required Fields'}
              {fileMappingStatus.status === 'missing-required-metadata' &&
                'Missing Required Metadata'}
              {fileMappingStatus.status === 'partial' &&
                `${Math.round(fileMappingStatus.score * 100)}% - Partial Match`}
              {fileMappingStatus.status === 'poor' &&
                `${Math.round(fileMappingStatus.score * 100)}% - Poor Match`}
            </Badge>

            {/* Detailed Status Info */}
            <Text
              size="1"
              color="textPrimary"
              align="center"
              style={{ fontStyle: 'italic' }}
            >
              {fileMappingStatus.details}
            </Text>

            {/* Debug Metrics (only for non-excellent status) */}
            {fileMappingStatus.status !== 'excellent' &&
              fileMappingStatus.metrics && (
                <div
                  style={{
                    display: 'flex',
                    gap: '12px',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                  }}
                >
                  <Text size="1" color="gray">
                    File columns:{' '}
                    {Math.round(
                      fileMappingStatus.metrics.fileColumnCoverage * 100
                    )}
                    %
                  </Text>
                  <Text size="1" color="gray">
                    Required fields:{' '}
                    {Math.round(
                      fileMappingStatus.metrics.requiredFieldCoverage * 100
                    )}
                    %
                  </Text>
                  {fileMappingStatus.metrics.unmappedFileColumns > 0 && (
                    <Text size="1" color="orange">
                      {fileMappingStatus.metrics.unmappedFileColumns} unmapped
                    </Text>
                  )}
                  {fileMappingStatus.metrics.missingRequiredFields > 0 && (
                    <Text size="1" color="red">
                      {fileMappingStatus.metrics.missingRequiredFields} required
                      missing
                    </Text>
                  )}
                </div>
              )}
          </div>
        )}
      </HoverCardContent>
    </HoverCardRoot>
  );
};
